import { useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../../assets/logo.svg';
import { DataProvider } from '../../../store/ContextProvider';
import { FaHome } from "react-icons/fa";
import { useMemo } from "react";
import { ProgressBar } from '../ProgressBar';


export const Layout = ({ children, candidate }) => {
  const { currentUser } = useContext(DataProvider);
  const navigate = useNavigate();
  const location = useLocation();
  const activelink = location.pathname;

  const navLink = useMemo(() => [
    {
      label: 'Skills & Resume',
      page: 'skills',
      step: 1,
      progress: !!(candidate?.isSkills || (candidate?.skills?.length > 0)),
      processing: "Flex Your Strengths, Skills Get You Shortlisted."
    },
    {
      label: 'Personal Info',
      page: 'personalInformation',
      step: 2,
      progress: !!(candidate?.isPersonalInformation || (candidate?.languages?.length > 0)),
      processing: "Kickstart Your Profile—Basics That Open Career Doors."
    },
    {
      label: 'Summary & Residential',
      page: 'profilesummery',
      step: 3,
      progress: !!(candidate?.isSummeryInformation || (candidate?.presentAddress?.addressLine1)),
      processing: "Your Story Matters—Let Recruiters Know Who You Are."
    },
    {
      label: 'Education',
      page: 'education',
      step: 4,
      progress: !!(candidate?.isEducation || (candidate?.educations?.length > 0)),
      processing: "Show Where You’ve Learned, Boosts Trust Instantly."
    },
    {
      label: 'Experience',
      page: 'experience',
      step: 5,
      progress: !!(candidate?.isExperience || (candidate?.experiences?.length > 0) || (candidate?.experiences?.isFresher)),
      processing: "Share What You’ve Done, Every Role Adds Value."
    },
    {
      label: 'Highlights',
      page: 'highlights',
      step: 6,
      progress: !!(
        candidate?.iscertifications || candidate?.isProjects || candidate?.isSocial ||
        (candidate?.certifications?.length > 0) ||
        (candidate?.projects?.length > 0) ||
        (candidate?.socialProfiles?.length > 0)
      ),
      processing: "Stand Out—Achievements Make Your Profile Shine."
    },
    {
      label: 'Preferences',
      page: 'preferences',
      step: 7,
      progress: !!(candidate?.isPreference || (candidate?.preferredJob?.length > 0)),
      processing: "Tell Us What You Want, Get Jobs Tailored To You."
    }
  ], [candidate]);


  // Default redirect from / to first step
  useEffect(() => {
    if (activelink === "/") {
      navigate(`/update-account/personalInformation?userId=${currentUser._id}`, { replace: true });
    }
  }, [activelink, navigate, currentUser]);

  useEffect(() => {
    const nextStepIndex = navLink.findIndex(nav => nav.progress === false || !nav.progress);
    if (nextStepIndex === -1) return;
    const currentStepIndex = navLink.findIndex(nav => `/update-account/${nav.page}` === activelink);
    if (currentStepIndex !== -1 && navLink[currentStepIndex].progress === false) {
      const nextNav = navLink[nextStepIndex];
      const nextPath = `/update-account/${nextNav.page}?userId=${currentUser._id}`;
      navigate(nextPath);
    }
  }, [candidate, activelink, navLink, navigate, currentUser]);

  return (
    <div className="flex h-screen justify-center overflow-hidden">
      {/* Sidebar */}
      <div className="hidden lg:flex w-[320px] h-screen bg-[#f8f8f8] border-r shadow-lg">
        <aside className="flex flex-col items-center h-full mt-3">
          {/* Logo */}
          <div className="sticky top-0 z-10 flex justify-center items-center h-[100px]">
            <img src={Logo} alt="Logo" className="w-[160px] h-auto object-contain cursor-pointer rounded-xl" onClick={() => window.location.href = "/"} />
          </div>

          {/* Navigation Steps */}
          <div className="overflow-y-auto px-10 py-5">
            {navLink.map((nav, i) => {
              const navPath = `/update-account/${nav.page}?userId=${currentUser._id}`;

              // Tick mark only if step completed
              const showTick = nav.progress === true;

              return (
                <div
                  key={i}
                  className={`flex items-center gap-4 py-2 px-3 rounded-xl mb-2 cursor-pointer transition-all duration-200
                  ${activelink === `/update-account/${nav.page}`
                      ? 'bg-white text-[#2C25C4] shadow-lg'
                      : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  onClick={() => navigate(navPath)}
                >
                  <div
                    className={`flex items-center justify-center w-5 h-5 rounded-full text-sm font-bold
                    ${activelink === `/update-account/${nav.page}`
                        ? 'bg-[#2C25C4] text-white shadow-md' // active
                        : showTick
                          ? 'bg-[#2C25C4] text-white border font-medium border-[#2C25C4]'
                          : 'border border-gray-400 text-gray-600'
                      }`}
                  >
                    {showTick ? <span>{i + 1}</span> : `${nav.step}`}
                  </div>

                  <span
                    className={`text-base truncate ${activelink === `/update-account/${nav.page}`
                      ? 'text-[#2C25C4]'
                      : showTick ? "text-[#2C25C4]" : ""
                      }`}
                  >
                    {nav.label}
                  </span>
                </div>
              );
            })}
          </div>
          <a className='text-white bg-primary-300 px-3 py-1 rounded-lg w-fit font-semibold' href="/">Back to Homepage</a>
        </aside>
      </div>
      {/* Mobile Bottom Navbar */}
      <div className='block lg:hidden fixed bottom-0 py-4 bg-white w-full z-50 border-t shadow-lg'>
        <div className='flex justify-between px-4'>
          {navLink.map((nav, i) => {
            const navPath = `/update-account/${nav.page}?userId=${currentUser._id}`;
            const isAllowed = i === 0 ? true : navLink[i - 1].progress === true;
            const showTick = nav.progress === true;
            const isActive = activelink === `/update-account/${nav.page}`;

            return (
              <div
                key={i}
                className='flex flex-col items-center cursor-pointer'
                onClick={() => {
                  if (isAllowed) navigate(navPath);
                  else alert(`Please complete Step ${i} before moving to Step ${i + 1}`);
                }}
              >
                <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold
            ${isActive ? 'bg-[#2C25C4] text-white shadow-md' : 'border border-gray-400 text-gray-600'}
             `}>
                  {showTick ? <span>{i + 1}</span> : nav.step}
                  {/* {showTick ? <RxCheck size={18} /> : nav.step} */}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 h-full overflow-y-auto px-2 bg-[#ecf1ff] scroll-smooth scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
        <section className="max-w-7xl mx-auto  lg:px-4 w-full py-0 space-y-8">
          {/* Header */}
          <div className='sticky top-0 z-40 bg-[#ecf1ff] py-4 px-3 w-full pt-6'>
            <button className='absolute top-0 right-0 text-sm px-3 py-1 rounded-lg flex gap-1 items-center border border-primary-300 text-primary-300 hover:border-0 hover:text-white hover:bg-primary-300 mt-2 cursor-pointer' onClick={()=>window.location.href="/"}><FaHome className='text-xl'/> Home</button>
            <div className='flex items-end justify-between'>
              <div className="z-20 w-full pb-2">
                {navLink
                  .filter(nav => `/update-account/${nav.page}` === activelink)
                  .map((value, idx) => (
                    <div key={idx}>
                      <h2 className="font-semibold text-lg md:text-xl">{value.label}</h2>
                      <p className="text-sm mt-1">{value.processing}</p>
                    </div>
                  ))
                }
              </div>
              <p className='text-sm text-gray-500 text-center flex gap-1 items-center whitespace-nowrap'>Profile Score: <span className='text-primary-500 text-lg font-bold'>{candidate?.score ? candidate?.score : 0}%</span></p>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 relative">
              <ProgressBar value={candidate?.score} />
            </div>
          </div>
          {/* Children Content */}
          <div>{children}</div>
        </section>
      </div>
    </div>
  );
};




